import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  Project,
  ProjectManagerService,
  CommunicationManagerService,
  ModalService,
  EnumService,
  StubProject,
  CompanyService,
  LocalStorageService,
  EntityService,
  StripeService,
  ActiveCompanySubscription,
  OpsSubscriptionModalService,
  SubscriptionStatus,
} from '@ops/shared-types';
import { OpsCustomCurrencyModalComponent, CustomCurrencyModalConfig } from '@ops/storybook';
import { MappedAddOn } from 'legacy/shared/shared-types/src/lib/models/stripe-product-addons.model';
import { forkJoin, of, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.component.html',
  styleUrls: ['./create-project.component.scss'],
})
export class CreateProjectComponent implements OnInit {
  project: Project = { ...StubProject };

  companyName;

  companies: Array<{ _id: string; name: string; shortName: string }>;
  accountOptions: Array<string>;
  projectPurposeOptions: Array<string>;
  budgetTypeOptions: Array<string>;
  projectJobOptions: Array<string>;
  serviceOptions: Array<string>;
  marketSegmentOptions: Array<string>;
  currencyOptions: Array<string>;

  expectedBudget: string;
  currencyRate: string;

  clients: Array<{ name: string; id: string }> = [];
  client: string;
  billingCompany: string;

  isDifferentBilling: boolean;

  isSavingInProgress: boolean = false;

  subscriptions: Array<Subscription> = [];

  companyId: string;

  companyCurrency?: string;

  // Properties for subscription limit handling
  mappedAddOns: MappedAddOn[] = [];
  confirmSubscription: Subscription;
  isLoading: boolean = false;

  projectScopeOptions: Array<string>;

  // Custom currency properties
  showCustomCurrencyForm: boolean = false;
  customCurrencyConfig: CustomCurrencyModalConfig = {
    excludeCurrencyCodes: [],
    excludeReason: 'This currency is already in use',
    onSuccess: {
      updateField: 'equivalentCurrency',
      additionalData: { closeDropdown: true }
    }
  };

  // clientLabel: string = "Client";

  constructor(
    private router: Router,
    private pm: ProjectManagerService,
    private cm: CommunicationManagerService,
    private es: EntityService,
    private modalService: ModalService,
    private enumService: EnumService,
    private localStorageService: LocalStorageService,
    private companyService: CompanyService,
    private translateService: TranslateService,
    private stripeService: StripeService,
    private subscriptionModalService: OpsSubscriptionModalService
  ) {
    this.companyCurrency =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode;
  }

  ngOnInit(): void {
    this.isSavingInProgress = false;
    this.project = { ...StubProject };
    const sub = this.cm.get('enum').subscribe((enums: any) => {
      this.projectPurposeOptions = enums['purpose'];
      this.budgetTypeOptions = ['S', 'M', 'L']; //enums['budgetType'];
      this.projectJobOptions = enums['projectJob'];
      this.serviceOptions = enums['service'];
      this.marketSegmentOptions = enums['marketSegment'];
    });
    this.projectScopeOptions = this.enumService.getProjectScopeOptions();

    // this.clientLabel = this.translateService.instant('labels.clientLabel');
    // If you need to wait for translation files to load
    // this.translateService.get('labels.clientLabel').subscribe((translatedText: string) => {
    //   this.clientLabel = translatedText;
    // });
    this.subscriptions.push(sub);

    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions(true),
      'None', // TODO: translate this
    ];

    const cName = this.localStorageService.getCurrentCompany()?.name ?? '';
    this.companyId = this.localStorageService.getCurrentCompany()?._id;

    //TODO: create service method
    const subCompany = this.companyService
      .getCompaniesDetailed()
      .subscribe((companies: any) => {
        this.companies = companies;
        this.accountOptions = companies.map((c: any) => c.name);

        this.companyName = this.companies.find(
          (companyItem) => companyItem.shortName === cName
        ).name;

        this.project.company = this.companies.find(
          (companyItem) => companyItem.shortName === cName
        )._id;
      });
    this.subscriptions.push(subCompany);

    this.project.service = [];
    this.project.shootingDates = [];
    this.project.preparationDates = [];

    //TODO: create client service
    if (this.companyId) {
      const subClient = this.cm
        .get(`registryEntity/all/Client`, [
          { headerName: 'companyId', headerValue: this.companyId },
        ])
        .subscribe((clients: any) => {
          clients.forEach((c: any) => {
            this.clients.push({ name: c.name, id: c._id });
          });
        });

      this.subscriptions.push(subClient);
    }

    // Load add-ons for subscription limit handling
    const addOnsSub = this.stripeService.getStripeProductsAddons().subscribe((addOns: MappedAddOn[]) => {
      this.mappedAddOns = addOns;
    });
    this.subscriptions.push(addOnsSub);
  }

  openModal(id: string) {
    this.modalService.open(id);
  }
  closeModal(id: string) {
    this.modalService.close(id);
  }

  //TODO: unify keyboard verification to use code or keyCode, or both, research which is optimal. Maybe create a keyboard utility class
  keyPressAction(keyPressed: any, value: string) {
    if (keyPressed.code.toLowerCase() === 'tab') {
      this.modalService.closeAll();
    }
    if (![13, 32].includes(keyPressed.keyCode)) {
      return;
    }
    this.openModal(value);
  }

  //TODO: onFocusOut for field value updating? is it possible to pass value with onfocusout? console log the event
  onInputName(event: any): void {
    this.project.name = event.target.value;
  }

  onInputBilling(event: any) {
    this.billingCompany = event.target.value;
  }

  onInputClient(event: any): void {
    //TODO: should be autofill, get client list and add possibility to add new client
    //new client only has name, and notification icon appears on the registry widget to
    //mark that a client exists that has missing fields
    //also change project.client to match the structure of the backend object
    this.client = event.target.value;
  }

  onInputProjectCode(event: any): void {
    this.project.code = event.target.value?.toUpperCase();
  }

  onInputExpectedBudget(event: any): void {
    this.project.expectedBudget = +event.target.value.replaceAll(',', '');
  }

  onInputCurrencyRate(value: number) {
    this.project.currencyRate = value || 1;
  }

  // Custom currency methods
  toggleCustomCurrencyForm(): void {
    this.showCustomCurrencyForm = !this.showCustomCurrencyForm;
  }

  onCustomCurrencyAdded(event: { 
    currencyCode: string; 
    updateField?: string; 
    additionalData?: any; 
  }): void {
    // Refresh currency options to include the new custom currency
    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions(true),
      'None', // TODO: translate this
    ];
    
    // Auto-select the newly added currency
    this.project.equivalentCurrency = event.currencyCode;
    
    // Close the modal
    this.showCustomCurrencyForm = false;
    
    // Close the dropdown modal if specified
    if (event.additionalData?.closeDropdown) {
      this.modalService.close('dropdown-currency');
    }
  }

  onCustomCurrencyModalClosed(): void {
    this.showCustomCurrencyForm = false;
  }

  onSelectAccount(value: any) {
    this.project.company = this.companies.find(
      (companyItem) => companyItem.name === value
    )._id;
    this.companyName = value;
  }

  onSelectPurpose(value: any) {
    this.project.purpose = value;
  }

  onSelectBudgetType(value: any) {
    this.project.budgetType = value;
  }

  onSelectprojectJob(value: any) {
    this.project.projectJob = value;
  }

  onSelectService(value: any) {
    const existing = this.project.service?.find((s) => s === value);
    if (existing) {
      this.project.service = this.project.service?.filter((s) => s !== value);
      return;
    }
    this.project.service?.push(value);
  }

  onSelectMarketSegment(value: any) {
    this.project.marketSegment = value;
  }

  onSelectCurrency(value: string) {
    if (value === '+ Add New') {
      this.toggleCustomCurrencyForm();
      return;
    }
    
    if (value?.toUpperCase() == 'NONE') {
      this.project.equivalentCurrency = undefined;
      return;
    }
    this.project.equivalentCurrency = value;
  }

  onToggleScope(scopeKey: string) {
    this.project.scope.find(s => s === scopeKey) 
      ? this.project.scope = this.project.scope.filter(s => s !== scopeKey)
      : this.project.scope.push(scopeKey);
  }

  onCancel() {
    // Reset loading states before navigating away
    this.isLoading = false;
    this.isSavingInProgress = false;
    
    // Clean up any active subscriptions
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    
    this.router.navigate(['dashboard']);
  }

  // Helper function to find a client by name
  findClientByName(name) {
    return this.clients.find(
      (client) => client.name.trim().toLowerCase() === name.trim().toLowerCase()
    );
  }

  // Main function
  handleClients(projectInfo, goToM1) {
    const clientName = this.client.trim();
    const billingCompanyName = this.billingCompany?.trim();

    const findOrCreateClient = (name) => {
      const matchedClient = name ? this.findClientByName(name) : null;
      return matchedClient
        ? of(matchedClient)
        : this.cm
            .postAndGetJson(
              'registryEntity',
              {
                name,
                type: 'Client',
                company: this.companyId,
              },
              [{ headerName: 'companyId', headerValue: this.companyId }]
            )
            .pipe(
              tap((newClient) => {
                this.clients.push({ name: newClient.name, id: newClient._id });
              })
            );
    };

    const client$ = findOrCreateClient(clientName);
    const billingClient$ = this.isDifferentBilling
      ? findOrCreateClient(billingCompanyName)
      : of(null);

    forkJoin({ client: client$, billingCompany: billingClient$ })
      .pipe(
        switchMap(({ client, billingCompany }) => {
          projectInfo.client = client.id;
          if (billingCompany) {
            projectInfo.billingCompany = billingCompany.id;
          }
          return this.pm.getNewProject(projectInfo, this.companyId);
        })
      )
      .subscribe({
        next: (projectId) => {
          this.project._id = projectId;
          if (goToM1) {
            this.onSelectProject(projectId);
          } else {
            this.onStoreProject(projectId);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.isSavingInProgress = false;
          window.alert(this.translateService.instant('windowAlert.generic.error', { error: JSON.parse(error.error).message }));
        },
      });
  }

  onSaveProject(goToM1?: boolean) {
    this.isLoading = true;
    this.checkIfSubscriptionHasReachedLimit().then(canCreate => {
      if (!canCreate) {
        this.isLoading = false;
        return;
      }

      if (this.isSavingInProgress) {
        this.isLoading = false;
        return;
      }

      if (
        !!!this.project.name ||
        !!!this.project.code ||
        !!!this.project.company
      ) {
        this.isLoading = false;
        return;
      }

      if (
        !this.project.currencyRate &&
        this.project.equivalentCurrency &&
        this.project.equivalentCurrency?.toUpperCase() != 'NONE'
      ) {
        window.alert(
          this.translateService.instant('auth.createProject.exchangeRateRequired')
        );
        this.isLoading = false;
        return;
      }

      this.isSavingInProgress = true;

      const projectInfo: any = {
        name: this.project.name,
        code: this.project.code,
        company: this.project.company,
        scope: this.project.scope,
        purpose: this.project.purpose,
        budgetType: this.project.budgetType,
        projectJob: this.project.projectJob,
        service: this.project.service,
        marketSegment: this.project.marketSegment,
        shootingDates: this.project.shootingDates,
        preparationDates: this.project.preparationDates,
        expectedBudget: +(this.project.expectedBudget ?? 0)
          .toString()
          .replace(/(,)/gi, ''),
        projectCurrency: this.companyCurrency,
        equivalentCurrency:
          this.project.equivalentCurrency?.toUpperCase() === 'NONE'
            ? undefined
            : this.project.equivalentCurrency,
        currencyRate: this.project.currencyRate || 1,
        status: '0',
      };

      if (!projectInfo.purpose) {
        delete projectInfo.purpose;
      }

      if (!projectInfo.marketSegment) {
        delete projectInfo.marketSegment;
      }

      this.handleClients(projectInfo, goToM1);
    });
  }

  onStoreProject(guid: string) {
    const sub = this.pm.getProjectById(guid).subscribe((project: Project) => {
      // console.log('navigation return selected project on version click');
      this.localStorageService.setProjectById(guid, project);

      this.router.navigate(['dashboard']);
    });

    this.subscriptions.push(sub);
  }

  onSelectProject(guid: string) {
    const sub = this.pm.getProjectById(guid).subscribe((project: Project) => {
      // console.log('navigation return selected project on version click');
      if (
        this.project.company !==
        this.localStorageService.getCurrentCompany()?._id
      ) {
        const selectedCompanyFilter = this.companies.find(
          (companyFilter) => companyFilter._id === this.project.company
        );

        this.localStorageService.setCurrentCompany(selectedCompanyFilter);
        this.companyService.selectedCompany$.next(selectedCompanyFilter);
      }

      this.pm.setCurrentProject(project);

      // Clear all modals before navigation to prevent conflicts
      this.modalService.clearAll();
      
      setTimeout(() => {
        this.router.navigate(['project-estimate/project-info']);
      });
    });

    this.subscriptions.push(sub);
  }

  //multiselection project dates
  onToggleProjectDate(dates: Array<Date>) {
    this.project.shootingDates = dates.map(
      (d) => d.toLocaleDateString('en-US') as any
    );
  }

  //multiselection preparation dates
  onTogglePreparationDate(dates: Array<Date>) {
    this.project.preparationDates = dates.map(
      (d) => d.toLocaleDateString('en-US') as any
    );
  }

  toggleBilling() {
    this.isDifferentBilling = !this.isDifferentBilling;

    if (!this.isDifferentBilling) {
      this.billingCompany = '';
    }
  }

  //TODO: extract to utility class
  isDateInArray(array: Array<Date>, value: Date) {
    if (array && value) {
      const l = !!array.find((item: Date) => item.getTime() == value.getTime());
      return l;
    }
  }

  get companyBaseCurrency() {
    return this.companyCurrency;
  }

  checkIfSubscriptionHasReachedLimit(): Promise<boolean> {
    return new Promise((resolve) => {
      const companyId = this.localStorageService.getCurrentCompany()?._id;
      if (!companyId) {
        resolve(true); // Allow creation if no company is selected
        return;
      }

      const subscription = this.stripeService.getActiveCompoanySubscription().subscribe({
        next: (subscriptionData: ActiveCompanySubscription) => {
          if (subscriptionData && subscriptionData.currentProjects >= subscriptionData.maxRealProjectsInCompany) {
            // Show confirmation modal for purchasing additional project slot
            this.showPurchaseProjectSlotModal(subscriptionData, resolve);
          } else {
            resolve(true); // Limit not reached, allow creation
          }
          subscription.unsubscribe();
        },
        error: (error) => {
          console.error('Error checking subscription limits:', error);
          resolve(true); // Allow creation if we can't check limits
        }
      });
    });
  }

  showPurchaseProjectSlotModal(subscriptionData: ActiveCompanySubscription, resolve: (value: boolean) => void) {
    const projectAddOn = this.mappedAddOns.find(addOn => addOn.name === 'Add extra project');
    
    if (!projectAddOn) {
      window.alert(this.translateService.instant('auth.createProject.subscriptionLimitReached'));
      resolve(false);
      return;
    }

    const titleText = this.translateService.instant('common.confirmation');
    const confirmationText = this.translateService.instant('auth.createProject.purchaseProjectSlotConfirmation');
    
    this.showConfirmationModal(
      titleText,
      confirmationText,
      () => this.onConfirmedPurchaseProjectSlot(projectAddOn, subscriptionData, resolve),
      () => this.onCancelledPurchaseProjectSlot(resolve)
    );
  }

  onConfirmedPurchaseProjectSlot(addOn: MappedAddOn, subscriptionData: ActiveCompanySubscription, resolve: (value: boolean) => void) {
    // Determine the subscription interval from the subscription data
    const subscriptionInterval = subscriptionData.stripeData?.stripePrice?.recurring?.interval;
    const planCurrency = subscriptionData.stripeData?.stripePrice?.currency;
    if (!subscriptionInterval) {
      console.error('Could not determine subscription interval');
      window.alert(this.translateService.instant('auth.createProject.addOnPriceNotFound'));
      resolve(false);
      return;
    }

    // Get the price ID based on the subscription interval
    let priceId: string | undefined;
    const currency = planCurrency.toUpperCase() || 'USD';
    
    if (subscriptionInterval === 'month') {
      priceId = addOn.monthly[currency]?.id;
    } else if (subscriptionInterval === 'year') {
      priceId = addOn.yearly[currency]?.id;
    }
    
    if (!priceId) {
      console.error(`No ${subscriptionInterval} price found for currency ${currency}`);
      window.alert(this.translateService.instant('auth.createProject.addOnPriceNotFound'));
      resolve(false);
      return;
    }
 
    this.stripeService.addAddOnOnSubscriptionId(subscriptionData._id, priceId).subscribe({
      next: (resp: any) => {
        const respJson = resp ? JSON.parse(resp || '{}') : {};

        // Show success message and allow project creation
        this.showSuccessModal();
        resolve(true);
      },
      error: (errorResp) => { 
        console.error('Error purchasing project slot:', errorResp);
        this.isLoading = false;
        window.alert(this.translateService.instant('auth.createProject.purchaseProjectSlotError'));
        resolve(false);
      }
    });
  }

  onCancelledPurchaseProjectSlot(resolve: (value: boolean) => void) {
    // User cancelled the purchase - reset loading state and block project creation
    this.isLoading = false;
    resolve(false);
  }

  showConfirmationModal(
    titleText: string,
    bodyText: string,
    confirmationCallBack: () => void,
    cancellationCallBack?: () => void
  ) {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.isLoading = false;
    this.confirmSubscription = this.subscriptionModalService
      .openModal({
        statusType: SubscriptionStatus.CONFIRMATION,
        modalTitle: titleText,
        modalText: bodyText,
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          // User confirmed
          this.isLoading = true;
          confirmationCallBack();
        } else {
          // User declined - call cancellation callback if provided, otherwise reset loading state
          if (cancellationCallBack) {
            cancellationCallBack();
          } else {
            this.isLoading = false;
          }
        }
      });
  }

  showSuccessModal() {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.confirmSubscription = this.subscriptionModalService
      .openModal({
        statusType: SubscriptionStatus.SUCCESS,
        modalTitle: this.translateService.instant('common.success'),
        modalText: this.translateService.instant('auth.createProject.projectSlotPurchasedSuccessfully'),
      })
      .subscribe((confirmed) => {
        // Reset loading state regardless of confirmation
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
